import React from 'react';
import styles from './LoadingSplash.module.scss';

/**
 * LoadingSplash Component
 *
 * Displays a loading screen while critical translations are being preloaded
 * and as the Suspense fallback for lazy-loaded routes.
 * This component has no i18n (t) dependency to avoid showing untranslated keys
 * during the loading phase — the copy is resolved from a tiny local EN/AR map
 * keyed off the document language, which LanguageProvider keeps in sync.
 *
 * Design (council/Claude spec 2026-08-19): premium dark slate-900 background,
 * white Promotly logo with teal glow, single teal spinner ring, and
 * "Preparing your workspace..." copy (EN) / Arabic RTL equivalent.
 */
const SPLASH_TEXT = {
  en: 'Preparing your workspace...',
  ar: 'جارٍ تجهيز مساحة العمل...',
};

const getCurrentLang = () => {
  if (typeof document === 'undefined') return 'en';
  // LanguageProvider / i18next keep <html lang> in sync; "ar" (or ar-*) => Arabic
  const htmlLang = (document.documentElement.getAttribute('lang') || '').toLowerCase();
  if (htmlLang.startsWith('ar')) return 'ar';
  // Fallbacks: persisted language preference, then RTL direction
  try {
    const stored = (localStorage.getItem('i18nextLng') || localStorage.getItem('language') || '').toLowerCase();
    if (stored.startsWith('ar')) return 'ar';
  } catch (e) { /* storage unavailable */ }
  if (document.documentElement.getAttribute('dir') === 'rtl') return 'ar';
  return 'en';
};

const LoadingSplash = () => {
  const lang = getCurrentLang();
  const isRTL = lang === 'ar';

  return (
    <div className={styles.loadingSplash} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Subtle floating teal particles */}
      <div className={styles.particles}>
        <span className={styles.particle}></span>
        <span className={styles.particle}></span>
        <span className={styles.particle}></span>
      </div>

      <div className={styles.content}>
        {/* Logo/Brand — white Promotly logo with teal glow */}
        <div className={styles.logo}>
          <img
            src="/promotly-logo-transparent.png"
            alt="PROMOTLY"
            className={styles.logoIcon}
          />
        </div>

        {/* Teal spinner */}
        <div className={styles.spinner}></div>

        {/* Loading text — localized EN/AR without an i18n key lookup */}
        <p className={styles.loadingText}>{SPLASH_TEXT[lang]}</p>
      </div>
    </div>
  );
};

export default LoadingSplash;
